import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Editorial white PageHero used on all non-home routes.
 * - IBM Plex Mono eyebrow (brand purple)
 * - Fraunces headline (with optional italic accent word via `gradient` prop, coloured purple)
 * - Inter Tight subhead
 * - Breadcrumb in mono
 */
export default function PageHero({
  eyebrow,
  title,
  gradient,
  subtitle,
  breadcrumb = [],
  align = "left",
}) {
  const reduced = useReducedMotion();
  return (
    <section
      data-testid="page-hero"
      className="relative pt-32 md:pt-40 pb-16 md:pb-20 bg-white border-b border-[rgba(11,11,16,0.08)]"
    >
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(91,42,134,0.05),transparent_70%)] pointer-events-none" aria-hidden />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(245,184,65,0.07),transparent_70%)] pointer-events-none" aria-hidden />

      <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10">
        {breadcrumb.length > 0 && (
          <nav
            className="mb-8 flex items-center gap-2 font-editorial-mono text-[10.5px] text-[#8A8A8A] flex-wrap"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-[#5B2A86] transition-colors" data-testid="breadcrumb-home">
              HOME
            </Link>
            {breadcrumb.map((c) => (
              <span key={c.label} className="flex items-center gap-2">
                <ChevronRight className="h-3 w-3 text-[rgba(11,11,16,0.30)]" />
                {c.to ? (
                  <Link to={c.to} className="hover:text-[#5B2A86] transition-colors">
                    {c.label.toUpperCase()}
                  </Link>
                ) : (
                  <span className="text-[#0B0B10]">{c.label.toUpperCase()}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: EASE }}
          className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-4xl"}
        >
          {eyebrow && (
            <div className="font-editorial-mono text-[10.5px] text-[#5B2A86] mb-5">
              {eyebrow}
            </div>
          )}
          <h1
            className="font-editorial-display leading-[0.98] tracking-[-0.028em] text-[#0B0B10]"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 5rem)", fontVariationSettings: '"SOFT" 30, "opsz" 144', fontWeight: 400 }}
          >
            {title}
            {gradient && (
              <>
                {" "}
                <span className="font-editorial-italic text-[#5B2A86]" style={{ fontStyle: "italic" }}>
                  {gradient}
                </span>
              </>
            )}
          </h1>
          {subtitle && (
            <p className="mt-6 max-w-[680px] text-[16px] md:text-[18px] leading-[1.6] text-[#4A4A55]">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
